import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'
import { installApiBaseFallback } from './api-base-fallback'

const API = process.env.REACT_APP_API_URL

class BaseService<T> {
  private api = axios.create({
    baseURL: API,
    timeout: 100000,
    headers: {
      'content-type': 'application/json; charset=UTF-8',
    },
    responseType: 'json',
  })

  constructor() {
    axiosRetry(this.api, {
      retries: 3,
      retryDelay: (retryCount: number) => {
        return Math.min(1000 * Math.pow(2, retryCount), 10000);
      },
      retryCondition: (error: AxiosError) => {
        const requestUrl = String(error.config?.url ?? '');
        const isFotoUsuarioRequest = requestUrl.includes('/api/v1/foto') || requestUrl.includes('api/v1/foto');
        if (isFotoUsuarioRequest) {
          // Foto de usuário pode não existir; evitar múltiplas tentativas.
          return false;
        }
        const isAnexoDownload = requestUrl.includes('anexos/download');
        if (isAnexoDownload && error.response) {
          // 4xx/5xx no download não costumam “curar” com retry; evita spam no console e carga no servidor.
          return false;
        }
        return (
          axiosRetry.isNetworkOrIdempotentRequestError(error) ||
          error.code === 'ECONNABORTED' ||
          error.code === 'ERR_CONNECTION_CLOSED' ||
          error.code === 'ETIMEDOUT' ||
          error.code === 'ECONNRESET' ||
          (error.response && (
            error.response.status === 429 ||
            error.response.status === 500 ||
            error.response.status === 502 ||
            error.response.status === 503 ||
            error.response.status === 504
          ))
        ) || false;
      }
    });

    this.api.interceptors.response.use(this.handleSuccess, this.handleError)
    this.api.interceptors.request.use(
      async (config) => {
        config.headers.Authorization =  `Bearer ${sessionStorage.getItem('sgo-access-token')}`
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )
    // Último interceptor de resposta: em erro, tenta a outra base (API ↔ FORM) antes do handleError.
    installApiBaseFallback(this.api)
  }

  private handleSuccess(response: AxiosResponse<T>) {
    return response
  }

  private handleError(error: AxiosError) {
    // Falhas de autenticação são tratadas pelo AuthProvider, sem registrar o request/bearer.
    if (String(error.config?.url ?? '').includes('/protocol/openid-connect/userinfo')) {
      return Promise.reject(error)
    }
    // Verificar se é um erro de rede em requisição de blob (pode ser silencioso)
    const isBlobRequest = error.config?.responseType === 'blob';
    const isNetworkError = error.code === 'ERR_NETWORK' || 
                          error.code === 'ERR_CONNECTION_REFUSED' ||
                          error.code === 'ERR_CONNECTION_CLOSED' ||
                          (!error.response && error.request);
    const requestUrl = String(error.config?.url ?? '');
    const isFotoUsuarioRequest = requestUrl.includes('/api/v1/foto') || requestUrl.includes('api/v1/foto');
    const isLocaisAtendimentoRequest = requestUrl.includes('/api/atendimento/locais-atendimento');
    const responseContentType = String((error.response?.headers as any)?.['content-type'] ?? '');
    const responseData = error.response?.data as any;
    const isJsonBlobResponse = typeof Blob !== 'undefined' && responseData instanceof Blob && responseData.type.includes('application/json');
    const isFotoJsonBlob500 = isFotoUsuarioRequest && isBlobRequest && !!error.response && error.response.status >= 500 && (responseContentType.includes('application/json') || isJsonBlobResponse);

    // Não logar erros de rede em requisições de blob (como fotos de usuário)
    // que podem falhar silenciosamente
    if (
      (isBlobRequest && isNetworkError) ||
      isFotoUsuarioRequest ||
      isFotoJsonBlob500 ||
      (isLocaisAtendimentoRequest && isNetworkError)
    ) {
      return Promise.reject(error);
    }

    // Log comprehensive error information para outros erros
    const errorInfo: any = {
      message: error.message,
      code: error.code,
      config: error.config ? {
        url: error.config.url,
        method: error.config.method,
        baseURL: error.config.baseURL,
        timeout: error.config.timeout,
      } : undefined,
    };

    // Response error (server responded with error status)
    if (error.response) {
      errorInfo.status = error.response.status;
      errorInfo.statusText = error.response.statusText;
      errorInfo.data = error.response.data;
      errorInfo.headers = error.response.headers;
    }
    // Request error (request made but no response received)
    else if (error.request) {
      errorInfo.request = error.request;
      errorInfo.type = 'Network Error';
    }
    // Setup error (error in request configuration)
    else {
      errorInfo.type = 'Setup Error';
    }

    const isDicasTelaNotFound =
      requestUrl.includes('/api/dicas-tela/') && error.response?.status === 404
    if (isDicasTelaNotFound) {
      return Promise.reject(error)
    }

    console.error('API Error:', errorInfo);
    return Promise.reject(error)
  }

  public get(path: string, params?: AxiosRequestConfig, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.get<T>(path, { ...config, params })
  }

  public getWithConfig(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.get<T>(path, config)
  }

  public getFile(path: string, configs?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.request<T>({ url: path, method: 'get', ...configs })
  }

  public getBlob(path: string, configs?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.request<T>({ url: path, method: 'get',  responseType: 'blob', ...configs })
  }

  public post<Request = T>(path: string, data?: Request, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.post<T>(path, data, config)
  }

  public put(path: string, data: T, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.put<T>(path, data, config)
  }

  public patch(path: string, data?: T, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(path, data, config)
  }

  public delete(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(path, config)
  }

  /** POST com FormData (upload de arquivos); não envia Content-Type para o browser definir multipart/form-data com boundary */
  public postFormData(path: string, formData: FormData, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.post<T>(path, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': undefined,
      } as any,
    })
  }
}

export default BaseService

